import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { connect, type ConnectedProps } from "react-redux";
import DeleteContentConfirmationModal, {
  type RemoveContentObserverInput,
} from "../../../../components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import { SurveyFilterForm } from "../../../../components/filterForms";
import SearchInput from "../../../../components/searchInput/SearchInput";
import { ContentTypesEnum, RolePermissions, type SortingDirection } from "../../../../enums";
import { useRtn } from "../../../../hooks/useRtn";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "../../../../interfaces/onSelectionChanged";
import { bindAction } from "../../../../interfaces";
import Observable from "../../../../utils/Observable";
import SurveysNoResults from "../../../../views/library/surveys/Overview/SurveysNoResults";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import Restricted from "../../../Application/Restricted";
import { SurveyEditSuccess } from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import * as entityStateActions from "../state/actions/surveyEntityStateActions";
import * as surveysOverviewActions from "../state/actions/surveyOverviewActions";
import { surveysStateSelector } from "../state/surveyReducer";
import { changeVisibility, deleteSurvey, duplicateSurvey, getSurveys } from "../state/thunks/surveysOverviewThunk";
import { type SurveyOverview, type SurveyFiltersEnum } from "../types/models";
import CreateSurveyButton from "./containers/CreateSurveyButton";
import SurveysListContainer from "./containers/SurveysListContainer";
import { ColumnToParamMap, getColumnOptions } from "./getColumnOptions";
import SurveyOverviewHeader from "./SurveyOverviewHeader/SurveyOverviewHeader";
import { useNavigate } from "react-router";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { AssetVisibilityConfirmationModal } from "components/assetVisibilityConfirmationModal/AssetVisibilityConfirmationModal";
import { useSurveysQueryParams } from "./hooks/useSurveysQueryParams";
import type { FilterFormPropsBase } from "../../../../components/filterForms/types/props";

export type SurveysOverviewProps = PropsFromRedux;

export const SurveysOverview = (props: SurveysOverviewProps) => {
  const { resetOverview } = props;
  const navigate = useNavigate();
  const goToSurveyEdit = (payload: { id: number }) => navigate(`${payload.id.toString()}/configuration`);

  const {
    filters,
    search,
    page,
    pageSize,
    sortBy,
    sortDirection,
    setSearch,
    setFilters,
    resetFilters,
    setSort,
    setPagination,
    sortingColumnName: currentSortingColumnName,
  } = useSurveysQueryParams();

  const onTriggerSurveyRemovalObserver = useMemo(
    () => new Observable<(onRemoveConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void>(),
    [],
  );

  const onTriggerChangeVisibilityObserver = useMemo(
    () => new Observable<(onConfirm: () => void, visible: boolean, ids: number[]) => void>(),
    [],
  );

  const [purchasedSelectedCount, setPurchasedSelectedCount] = useState(0);
  const [undeletableSelectedCount, setUndeletableSelectedCount] = useState(0);
  const [draftSelectedCount, setDraftSelectedCount] = useState(0);
  const [hiddenSelectedCount, setHiddenSelectedCount] = useState(0);
  const [visibleSelectedCount, setVisibleSelectedCount] = useState(0);
  const [unpublishedSelectedCount, setUnpublishedSelectedCount] = useState(0);

  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();
  const searchWasJustChangedRef = useRef(false);
  const preventInitialReloadRef = useRef(true);
  const isSearchEmpty = isEmpty(search);

  const deleteContentEnabled = useFeatureFlag(FeatureFlags.DeleteContentWithDependenciesFeature);

  useRtn(SurveyEditSuccess, goToSurveyEdit);

  useEffect(() => {
    return () => {
      resetOverview();
    };
  }, [resetOverview]);

  useEffect(() => {
    // Prevent the page from being reset on initial load if we are navigating back to this page
    if (preventInitialReloadRef.current) {
      preventInitialReloadRef.current = false;
      return;
    }
    reloadListItemsRef.current && reloadListItemsRef.current(true);
  }, [search]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      searchWasJustChangedRef.current = true;
    },
    [setSearch],
  );

  const getSearchBar = () => (
    <SearchInput placeholder="Search for Surveys..." onChange={handleSearchChange} defaultValue={search} />
  );

  const handleEditClick = (id: number, isDraft?: boolean) => () => {
    if (!isDraft) props.fetchDraftSurveyEntity(id);
    goToSurveyEdit({ id });
  };

  const clearSelection = () => {
    props.onSelectedItemsChanged([]);

    setPurchasedSelectedCount(0);
    setUndeletableSelectedCount(0);
    setDraftSelectedCount(0);
    setHiddenSelectedCount(0);
    setVisibleSelectedCount(0);
    setUnpublishedSelectedCount(0);
  };

  const loadSurveyList = (
    skip: number = 0,
    top: number = 10,
    requestedSortingColumnName: string = "",
    orderDirection: SortingDirection | undefined = undefined,
  ) => {
    const trimmedSearch = search.trim();
    const normalizedColumnName = requestedSortingColumnName?.toLowerCase();
    const shouldIgnoreSort = searchWasJustChangedRef.current && trimmedSearch.length > 0;

    const resolvedOrderProp =
      !shouldIgnoreSort && normalizedColumnName
        ? ColumnToParamMap[normalizedColumnName as keyof typeof ColumnToParamMap]
        : undefined;
    const orderProp = shouldIgnoreSort ? undefined : resolvedOrderProp;
    const nextPageSize = top ?? pageSize;
    const nextPage = nextPageSize ? Math.floor((skip ?? 0) / nextPageSize) + 1 : page;

    if (nextPageSize && (nextPage !== page || nextPageSize !== pageSize)) {
      setPagination(nextPage, nextPageSize);
    }

    if (!shouldIgnoreSort && orderProp && orderDirection) {
      if (orderProp !== sortBy || orderDirection !== sortDirection) {
        setSort(orderProp, orderDirection);
      }
    }

    props.getSurveys({
      skip,
      top: nextPageSize,
      sortBy: orderProp,
      sortOrder: orderDirection,
      searchText: search,
      filters,
    });

    searchWasJustChangedRef.current = false;
  };

  const handleToggleVisibility = (ids: number[], visible: boolean) => () => {
    onTriggerChangeVisibilityObserver.notify(
      () => {
        props.toggleVisibility(ids, visible, () => reloadListItemsRef.current?.(isSearchEmpty));
        clearSelection();
      },
      visible,
      ids,
    );
  };

  const handleDuplicateClick = (ids: number[]) => async () => {
    props.duplicateSurveys(ids, () => reloadListItemsRef.current?.(isSearchEmpty));
    clearSelection();
  };

  const handleDeleteClick = (ids: number[], flowsCount?: number, packsCount?: number) => () => {
    onTriggerSurveyRemovalObserver.notify(
      () => {
        props.deleteSurveys(ids, () => reloadListItemsRef.current?.(isSearchEmpty));
        clearSelection();
      },
      {
        selectedItemsCount: ids.length,
        flowsCount,
        packsCount,
      },
    );
  };

  const onSelectionChanged = (args: SelectionChangedArgs<SurveyOverview>) => {
    let purchasedCount = 0;
    let undeletableCount = 0;
    let draftCount = 0;
    let hiddenCount = 0;
    let visibleCount = 0;
    let unpublishedCount = 0;

    const onAdded = (survey: SurveyOverview) => {
      survey.isPurchased && ++purchasedCount;
      !survey.canBeDeleted && ++undeletableCount;
      survey.isDraft && ++draftCount;
      !survey.visibility && ++hiddenCount;
      survey.visibility && ++visibleCount;
      !survey.hasBeenPublished && ++unpublishedCount;
    };
    const onRemoved = (survey: SurveyOverview) => {
      survey.isPurchased && --purchasedCount;
      !survey.canBeDeleted && --undeletableCount;
      survey.isDraft && --draftCount;
      !survey.visibility && --hiddenCount;
      survey.visibility && --visibleCount;
      !survey.hasBeenPublished && --unpublishedCount;
    };

    HandleOnSelectionChanged(args, onAdded, onRemoved, clearSelection);

    setPurchasedSelectedCount(purchasedSelectedCount + purchasedCount);
    setUndeletableSelectedCount(undeletableSelectedCount + undeletableCount);
    setDraftSelectedCount(draftSelectedCount + draftCount);
    setHiddenSelectedCount(hiddenSelectedCount + hiddenCount);
    setVisibleSelectedCount(visibleSelectedCount + visibleCount);
    setUnpublishedSelectedCount(unpublishedSelectedCount + unpublishedCount);
  };

  const handleApplyFilter = useCallback(
    (nextFilters: FilterFormPropsBase<SurveyFiltersEnum>["filter"]) => {
      setFilters(nextFilters);
    },
    [setFilters],
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const getFilterForm = (filterFormProps: FilterFormPropsBase<SurveyFiltersEnum>) => (
    <SurveyFilterForm {...filterFormProps} />
  );

  return (
    <section className="nested-content">
      <SurveyOverviewHeader
        selectedIds={props.selectedIds}
        draftSelected={draftSelectedCount > 0}
        hiddenSelected={hiddenSelectedCount > 0}
        visibleSelected={visibleSelectedCount > 0}
        undeletableSelected={undeletableSelectedCount > 0}
        purchasedSurveySelected={purchasedSelectedCount > 0}
        renderCreateButton={() => <CreateSurveyButton />}
        surveyDuplicationHandler={handleDuplicateClick}
        surveyDeletionHandler={handleDeleteClick}
        clearSelection={clearSelection}
        onToggleVisibility={handleToggleVisibility}
        unpublishedSelected={unpublishedSelectedCount > 0}
      />
      <Restricted
        permissions={[RolePermissions.AssetsCreate, RolePermissions.AssetsManage]}
        renderContent={(hasPermission) => (
          <SurveysListContainer
            renderSearch={getSearchBar}
            noResultsContent={
              <SurveysNoResults
                filtered={!isEmpty(filters) || !isEmpty(search)}
                createSurveyButton={<CreateSurveyButton />}
              />
            }
            columnOptions={getColumnOptions({
              readonly: !hasPermission,
              visibility: !(visibleSelectedCount > 0 && hiddenSelectedCount > 0),
              deleteContentWithDependenciesFeatureEnabled: deleteContentEnabled,
              handleEditClick,
              handleDuplicateClick,
              handleDeleteClick,
              handleToggleVisibility,
            })}
            fetchData={loadSurveyList}
            defaultSortingColumnName={currentSortingColumnName}
            sortingDirection={sortDirection}
            onSelectedItemChanged={props.onSelectedItemsChanged}
            isSelectDisabled={() => !hasPermission}
            getFilterForm={getFilterForm}
            setReloadListItems={(reloadListItems) => (reloadListItemsRef.current = reloadListItems)}
            onSelectionChanged={onSelectionChanged}
            permissions={[RolePermissions.AssetsManage]}
            pagination={page}
            appliedFilter={filters}
            applyFilter={handleApplyFilter}
            resetFilter={handleResetFilters}
          />
        )}
      />
      <AssetVisibilityConfirmationModal
        observer={onTriggerChangeVisibilityObserver}
        assetType="survey"
        onClose={clearSelection}
      />
      <DeleteContentConfirmationModal
        contentType={ContentTypesEnum.Surveys}
        onTriggerRemoveContentObserver={onTriggerSurveyRemovalObserver}
        onClose={clearSelection}
      />
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const surveysState = surveysStateSelector(state);
  const overview = surveysState.overview;
  const surveysOverview = overview.surveysOverview;
  return {
    selectedIds: surveysOverview.selectedItems,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getSurveys: bindAction(getSurveys, dispatch),
  fetchDraftSurveyEntity: bindAction(entityStateActions.fetchDraftSurveyEntity, dispatch),
  onSelectedItemsChanged: bindAction(surveysOverviewActions.selectedItemsChangedAction, dispatch),
  resetOverview: bindAction(surveysOverviewActions.resetAction, dispatch),
  deleteSurveys: bindAction(deleteSurvey, dispatch),
  duplicateSurveys: bindAction(duplicateSurvey, dispatch),
  toggleVisibility: bindAction(changeVisibility, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SurveysOverview);
export default ConnectedComponent;
